import {BaseInterface} from './Request/BaseInterface';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs/Observable';
import {Factory as SuccessResponseFactory} from './Response/Success/Factory';
import {Success as ResponseSuccess} from './Response/Success';
import {Error as ResponseError} from './Response/Error';
import {Factory as ErrorResponseFactory} from './Response/Error/Factory';
import {Observer} from 'rxjs/Observer';
import {Factory as UnauthorizedErrorResponseFactory} from './Response/Error/Unauthorized/Factory';
import {Unauthorized as UnauthorizedError} from './Response/Error/Unauthorized';
import {Factory as InternalErrorResponseFactory} from './Response/Error/Internal/Factory';
import {Internal as InternalError} from './Response/Error/Internal';
import {MatSnackBar} from '@angular/material';
import {Injector} from '@angular/core';

export class Connector {

    private baseUrl: string = '';

    private httpClient: HttpClient = null;

    private successResponseFactory: SuccessResponseFactory = null;

    private errorResponseFactory: ErrorResponseFactory = null;

    private unauthorizedErrorResponseFactory: UnauthorizedErrorResponseFactory = null;

    private internalErrorResponseFactory: InternalErrorResponseFactory = null;

    private injector: Injector = null;

    // ########################################

    private matSnackBar: MatSnackBar = null;

    // ########################################

    private headers: HttpHeaders = null;

    // ########################################

    constructor(
        baseUrl: string,
        headers: HttpHeaders,
        httpClient: HttpClient,
        successResponseFactory: SuccessResponseFactory,
        errorResponseFactory: ErrorResponseFactory,
        unauthorizedErrorResponseFactory: UnauthorizedErrorResponseFactory,
        internalErrorResponseFactory: InternalErrorResponseFactory,
        injector: Injector
    ) {
        this.baseUrl = baseUrl;
        this.headers = headers;
        this.httpClient = httpClient;
        this.successResponseFactory = successResponseFactory;
        this.errorResponseFactory = errorResponseFactory;
        this.unauthorizedErrorResponseFactory = unauthorizedErrorResponseFactory;
        this.internalErrorResponseFactory = internalErrorResponseFactory;
        this.injector = injector;
    }

    // ########################################

    public process(command: BaseInterface): Observable<ResponseSuccess | ResponseError | UnauthorizedError | InternalError> {
        const method = command.getMethod();

        if (method === 'get') {
            return this.get(command.getUrl(), command.getParams());
        }

        if (method === 'post') {
            return this.post(command.getUrl(), command.getParams(), command.getBody());
        }

        if (method === 'put') {
            return this.put(command.getUrl(), command.getParams(), command.getBody());
        }

        if (method === 'delete') {
            return this.delete(command.getUrl(), command.getParams());
        }

        throw new Error(`Unknown command ${method}`);
    }

    // ########################################

    private get(url: string, params: string): Observable<ResponseSuccess | ResponseError | UnauthorizedError | InternalError> {
        const requestUrl = `${this.baseUrl}${url}${params}`;

        return new Observable<any>((observer) => {
            this.httpClient
                .get(
                    requestUrl,
                    {
                        observe: 'response',
                        headers: this.headers,
                        responseType: 'text'
                    }
                )
                .subscribe((httpResponse: HttpResponse<any>) => {

                    this.handlerSuccessResponse(httpResponse, observer);
                }, (httpErrorResponse: HttpErrorResponse) => {

                    this.handlerErrorResponse(httpErrorResponse, observer);
                });
        });
    }

    private post(url: string, params: string, body: any): Observable<ResponseSuccess | ResponseError | UnauthorizedError | InternalError> {
        const requestUrl = `${this.baseUrl}${url}${params}`;

        return new Observable<any>((observer) => {
            this.httpClient
                .post(
                    requestUrl,
                    body,
                    {
                        observe: 'response',
                        headers: this.headers,
                        responseType: 'text'
                    }
                )
                .subscribe((httpResponse: HttpResponse<any>) => {

                    this.handlerSuccessResponse(httpResponse, observer);
                }, (httpErrorResponse: HttpErrorResponse) => {

                    this.handlerErrorResponse(httpErrorResponse, observer);
                });
        });
    }

    private put(url: string, params: string, body: any): Observable<ResponseSuccess | ResponseError | UnauthorizedError | InternalError> {
        const requestUrl = `${this.baseUrl}${url}${params}`;

        return new Observable<any>((observer) => {
            this.httpClient
                .put(
                    requestUrl,
                    body,
                    {
                        observe: 'response',
                        headers: this.headers,
                        responseType: 'text'
                    }
                )
                .subscribe((httpResponse: HttpResponse<any>) => {

                    this.handlerSuccessResponse(httpResponse, observer);
                }, (httpErrorResponse: HttpErrorResponse) => {

                    this.handlerErrorResponse(httpErrorResponse, observer);
                });
        });
    }

    private delete(url: string, params: string): Observable<ResponseSuccess | ResponseError> {
        const requestUrl = `${this.baseUrl}${url}${params}`;

        return new Observable<any>((observer) => {
            this.httpClient
                .delete(
                    requestUrl,
                    {
                        observe: 'response',
                        headers: this.headers,
                        responseType: 'text'
                    }
                )
                .subscribe((httpResponse: HttpResponse<any>) => {

                    this.handlerSuccessResponse(httpResponse, observer);
                }, (httpErrorResponse: HttpErrorResponse) => {

                    this.handlerErrorResponse(httpErrorResponse, observer);
                });
        });
    }

    // ########################################

    private handlerSuccessResponse(httpResponse: HttpResponse<any>, observer: Observer<any>) {
        let httpResponseBody;
        let responseWarnings;

        try {

            httpResponseBody = JSON.parse(httpResponse.body);
        } catch (error) {

            throw new Error('Response is not valid json!');
        }

        if (httpResponse.status === 200) {

            if (httpResponseBody['errors']) {

                const errorResponse = this.errorResponseFactory.create(httpResponseBody['errors']);

                observer.error(errorResponse);
                observer.complete();

                return;
            }

            if (httpResponseBody['warnings']) {
                responseWarnings = httpResponseBody['warnings'];

                delete httpResponseBody['warnings'];
            }

            const response = this.successResponseFactory.create(httpResponseBody, responseWarnings);

            observer.next(response);
            observer.complete();

            return;
        } else {

            observer.complete();

            throw new Error('Unknown Success Response!');
        }
    }

    private handlerErrorResponse(httpErrorResponse: HttpErrorResponse, observer: Observer<any>) {
        if (httpErrorResponse.status === 0) {

            this.openNotification('No internet connection available');

            const errorResponse = this.errorResponseFactory.create(httpErrorResponse['errors']);

            observer.error(errorResponse);
            observer.complete();

            return;
        }

        let errorResponseBody;

        try {

            errorResponseBody = JSON.parse(httpErrorResponse.error);
        } catch (error) {

            throw new Error('Response is not valid json!');
        }

        if (httpErrorResponse.status === 404) {

            throw new Error('Error response! Not Found');
        } else if (httpErrorResponse.status === 401) {

            const unAuthError = this.unauthorizedErrorResponseFactory.create(errorResponseBody['errors']);

            observer.error(unAuthError);
            observer.complete();

            return;
        } else if (httpErrorResponse.status === 429) {

            throw new Error('Error ! Too many request');
        } else if (httpErrorResponse.status === 500) {

            const internalError = this.internalErrorResponseFactory.create(errorResponseBody['errors']);

            observer.error(internalError);
            observer.complete();

            return;
        }

        observer.complete();
        throw new Error('Unknown Error Response!');
    }

    // ########################################

    private openNotification(message: string): void {
        const snackBar: MatSnackBar = this.injector.get(MatSnackBar);

        snackBar.open(message, '', {
            duration: 4000,
            horizontalPosition: 'left',
            verticalPosition: 'bottom'
        });
    }

    // ########################################
}
